import React, { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { SelectorOption, StaticSelectFormDetails } from "../interfaces";
import { Theme, Checkbox, Box } from "@mui/material";

import { makeStyles } from "tss-react/mui";
import { useIntl } from "react-intl";
import { SELECTORS_OPTIONS } from "../utils";
import {
  FieldHelperProps,
  FieldInputProps,
  FieldMetaProps,
  useFormikContext,
} from "formik";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";
import { GenericObject } from "../../services/Price";
import { SX_CONTROLLED_AUTOCOMPLETE_SIZE } from "../styles";

const useStyles = makeStyles()((theme: Theme) => ({
  // Only the field's width: fill and outline come from the wrapper and the theme.
  field: {
    flex: "0 1 224px",
  },
}));

/**
 * Generate options for select input
 *
 * @param {string} param - Name of select to retrieve list of options
 * @returns Array of { key: string; label: string; } - list of options
 */
export const generateOptions = (param: string): SelectorOption[] => {
  const options = [];
  for (const [label, value] of Object.entries(SELECTORS_OPTIONS[param] || [])) {
    options.push({
      key: value as string,
      label: label,
    });
  }

  return options;
};

// Static selector, used with defined options
export const StaticSelector = ({
  filter,
  field,
  helpers,
  meta,
}: {
  filter: StaticSelectFormDetails;
  field: FieldInputProps<any>;
  helpers: FieldHelperProps<any>;
  meta: FieldMetaProps<any>;
}) => {
  const intl = useIntl();
  const { classes: basicsClasses } = useStyles();
  const { values, setFieldValue } = useFormikContext<any>();
  const options = useMemo(
    () => generateOptions(filter.secondaryKey || filter.key),
    [filter.key, filter.secondaryKey]
  );

  // Disabled input if input on which it depends is empty
  const isDisabled: boolean = useMemo(() => {
    let disabled = false;

    if (filter.readonly) {
      disabled = true;
    } else if (filter.enableField && values) {
      if (!values[filter.enableField as string]) {
        disabled = true;
      }
    }

    return disabled;
  }, [filter.enableField, filter.readonly, values]);

  const autocompleteValue = useMemo(() => {
    if (!filter.multiple) {
      return options.find((opt) => opt.key === field.value) || null;
    }

    if (!Array.isArray(field.value)) {
      return [];
    }

    return options.filter((opt) => field.value.includes(opt.key));
  }, [filter.multiple, field.value, options]);

  const handleChange = (newValue: any) => {
    // In case of filter.watchFilledField we doesn't want to clean value
    if (!filter.watchFilledField) {
      // Clean seletor (in case of dynamic values) if selector on which it depends change
      if (filter.watchedBy && Array.isArray(filter.watchedBy)) {
        filter.watchedBy.forEach((input) => {
          setFieldValue(input.split(".").join("__"), null);
        });
      } else if (filter.watchedBy) {
        // Get path of watchedBy input (take into account nested field)
        const fieldPathArray = field.name.split(".");
        const parentPath = fieldPathArray.slice(0, -1);
        const transformedWatchKey = filter.watchedBy.replace(/\./g, "__");
        const watchedFieldPath = [...parentPath, transformedWatchKey];

        setFieldValue(watchedFieldPath.join("."), null);
      }
    }

    if (filter.multiple && newValue) {
      return helpers.setValue(
        newValue.map((value: GenericObject) => value.key)
      );
    } else {
      return helpers.setValue(newValue?.key || null);
    }
  };

  return (
    <Box className={basicsClasses.field}>
      <CustomAutocomplete
        disableCloseOnSelect={filter.multiple}
        multiple={filter.multiple}
        getOptionKey={(option) => option.key}
        limitTags={
          filter.multiple && filter.maxTag && !filter.controlledInputSize
            ? filter.maxTag
            : filter.multiple && filter.controlledInputSize
            ? 1
            : undefined
        }
        disabled={filter.readonly || isDisabled}
        getOptionLabel={(option) => {
          return intl.formatMessage({ id: option.label }) || "";
        }}
        isOptionEqualToValue={(
          option: SelectorOption,
          value: SelectorOption | null
        ) => {
          return option.key === value?.key;
        }}
        options={options || []}
        onChange={(e, newValue) => handleChange(newValue)}
        translateTags
        placeholder={intl.formatMessage({ id: filter.label })}
        label={intl.formatMessage({ id: filter.label })}
        error={meta.touched && !!meta.error}
        helperText={meta.touched && meta.error}
        renderOptions={(props, option, state) => {
          return (
            <li {...props} key={option.key}>
              {filter.multiple && (
                <Checkbox sx={{ mr: 1, p: 0 }} checked={state.selected} />
              )}
              <FormattedMessage id={option.label} />
            </li>
          );
        }}
        size="small"
        sx={filter.controlledInputSize ? SX_CONTROLLED_AUTOCOMPLETE_SIZE : {}}
        value={autocompleteValue}
      />
    </Box>
  );
};
